import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, type RouteObject, RouterProvider } from 'react-router';
import { AuthProvider, SIGN_IN_REFUSED_EVENT } from './auth/AuthProvider';
import { RequireSignedIn, RequireSignedOut } from './auth/guards';
import { isSignInRefused } from './lib/api';
import { Chat } from './pages/Chat';
import { NotFound } from './pages/NotFound';
import { SignIn } from './pages/SignIn';
import { VerifyCode } from './pages/VerifyCode';
import { Welcome } from './pages/Welcome';

export const routes: RouteObject[] = [
  ...[
    '/tax-tools/:businessId',
    '/tax-tools/:businessId/jobs/:jobId',
    '/tax-tools/:businessId/artifacts/:artifactId',
  ].map((path) => ({
    path,
    lazy: async () => {
      const { SandboxProducts } = await import('./pages/SandboxProducts');
      return {
        Component: () => (
          <RequireSignedIn>
            <SandboxProducts />
          </RequireSignedIn>
        ),
      };
    },
  })),
  ...[
    '/capital-gains/:businessId',
    '/capital-gains/:businessId/jobs/:jobId',
    '/capital-gains/:businessId/artifacts/:artifactId',
  ].map((path) => ({
    path,
    lazy: async () => {
      const { SandboxIncome } = await import('./pages/SandboxIncome');
      return {
        Component: () => (
          <RequireSignedIn>
            <SandboxIncome />
          </RequireSignedIn>
        ),
      };
    },
  })),
  {
    path: '/gst-documents/:businessId/:artifactId',
    lazy: async () => {
      const { SandboxDocument } = await import('./pages/SandboxDocument');
      return {
        Component: () => (
          <RequireSignedIn>
            <SandboxDocument />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/',
    element: (
      <RequireSignedIn>
        <Chat />
      </RequireSignedIn>
    ),
  },
  {
    path: '/chats/:conversationId',
    element: (
      <RequireSignedIn>
        <Chat />
      </RequireSignedIn>
    ),
  },
  {
    path: '/welcome',
    element: (
      <RequireSignedOut>
        <Welcome />
      </RequireSignedOut>
    ),
  },
  {
    path: '/sign-in',
    element: (
      <RequireSignedOut>
        <SignIn />
      </RequireSignedOut>
    ),
  },
  {
    path: '/sign-in/code',
    element: (
      <RequireSignedOut>
        <VerifyCode />
      </RequireSignedOut>
    ),
  },
  // Loaded on demand: each is opened once, or rarely.
  {
    path: '/welcome/profile',
    lazy: async () => {
      const { CreateProfile } = await import('./pages/Profile');
      return {
        Component: () => (
          <RequireSignedIn step="profile">
            <CreateProfile />
          </RequireSignedIn>
        ),
      };
    },
  },
  // D9: the privacy notice changed since they agreed (or they haven't said they're 18 or older).
  {
    path: '/welcome/notice',
    lazy: async () => {
      const { UpdatedNotice } = await import('./pages/Profile');
      return {
        Component: () => (
          <RequireSignedIn step="notice">
            <UpdatedNotice />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/profile',
    lazy: async () => {
      const { EditProfile } = await import('./pages/Profile');
      return {
        Component: () => (
          <RequireSignedIn>
            <EditProfile />
          </RequireSignedIn>
        ),
      };
    },
  },
  // Privacy and data rights (Phase 17): open before agreeing to an updated notice, and the download while
  // the account waits to be deleted.
  {
    path: '/privacy-and-consents',
    lazy: async () => {
      const { Consents } = await import('./pages/Consents');
      return {
        Component: () => (
          <RequireSignedIn beforeNotice>
            <Consents />
          </RequireSignedIn>
        ),
      };
    },
  },
  ...(
    [
      ['/privacy-and-consents/data', 'WhatEkAppHolds'],
      ['/privacy-and-consents/download', 'DownloadData'],
      ['/privacy-and-consents/delete', 'DeleteAccount'],
      ['/privacy-and-consents/ask', 'AskAboutData'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/Privacy'))[name];
      return {
        Component: () => (
          <RequireSignedIn beforeNotice whileDeleting={name === 'DownloadData'}>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  ...(
    [
      ['/businesses', 'Businesses'],
      ['/businesses/add', 'AddBusiness'],
      ['/businesses/add/pan', 'FindByPan'],
      ['/businesses/add/confirm', 'ConfirmBusiness'],
      ['/businesses/:id', 'BusinessPage'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/Businesses'))[name];
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  {
    path: '/businesses/:id/connect',
    lazy: async () => {
      const { ConnectBusiness } = await import('./pages/Connect');
      return {
        Component: () => (
          <RequireSignedIn>
            <ConnectBusiness />
          </RequireSignedIn>
        ),
      };
    },
  },
  ...(
    [
      ['/filings', 'Filings'],
      ['/filings/:id', 'FilingReceipt'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/Filings'))[name];
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  ...[
    '/returns/:returnId/sales',
    '/returns/:returnId/sales/:invoiceId',
    // Gap 4: credit and debit notes, and the month's GST-free sales.
    '/returns/:returnId/notes/:noteId',
    '/returns/:returnId/gst-free',
    // Gap 4b: a filed sale corrected in GSTR-1A.
    '/returns/:returnId/correct',
    '/returns/:returnId/correct/:invoiceId',
  ].map((path) => ({
    path,
    lazy: async () => {
      const { Sales } = await import('./pages/Sales');
      return {
        Component: () => (
          <RequireSignedIn>
            <Sales />
          </RequireSignedIn>
        ),
      };
    },
  })),
  {
    path: '/imports/:importId/review',
    lazy: async () => {
      const { ImportReview } = await import('./pages/ImportReview');
      return {
        Component: () => (
          <RequireSignedIn>
            <ImportReview />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/imports/:importId/checks',
    lazy: async () => {
      const { Checks } = await import('./pages/Checks');
      return {
        Component: () => (
          <RequireSignedIn>
            <Checks />
          </RequireSignedIn>
        ),
      };
    },
  },
  // No records (Phase 16): a statement's every payment, and walk-in sales by rate.
  {
    path: '/imports/:importId/statement',
    lazy: async () => {
      const { Payments } = await import('./pages/Payments');
      return {
        Component: () => (
          <RequireSignedIn>
            <Payments />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/returns/:returnId/walk-in',
    lazy: async () => {
      const { WalkIn } = await import('./pages/WalkIn');
      return {
        Component: () => (
          <RequireSignedIn>
            <WalkIn />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/imports/:importId/rows/:rowId',
    lazy: async () => {
      const { FixRow } = await import('./pages/FixRow');
      return {
        Component: () => (
          <RequireSignedIn>
            <FixRow />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/chats',
    lazy: async () => {
      const { Chats } = await import('./pages/Chats');
      return {
        Component: () => (
          <RequireSignedIn>
            <Chats />
          </RequireSignedIn>
        ),
      };
    },
  },
  ...(
    [
      ['/notifications', 'Notifications'],
      ['/reminders', 'Reminders'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen =
        name === 'Notifications'
          ? (await import('./pages/Notifications')).Notifications
          : (await import('./pages/Reminders')).Reminders;
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  // People, invites and approvals (Phase 14). An invite link opened signed out comes back here after sign-in.
  {
    path: '/businesses/:id/people',
    lazy: async () => {
      const { People } = await import('./pages/People');
      return {
        Component: () => (
          <RequireSignedIn>
            <People />
          </RequireSignedIn>
        ),
      };
    },
  },
  {
    path: '/invites/:token',
    lazy: async () => {
      const { Invite } = await import('./pages/Invite');
      return {
        Component: () => (
          <RequireSignedIn>
            <Invite />
          </RequireSignedIn>
        ),
      };
    },
  },
  ...(
    [
      ['/approvals', 'Approvals'],
      ['/approvals/:id', 'Approval'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/Approvals'))[name];
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  // D6: after asking to delete the account, signed out.
  { path: '/goodbye', lazy: async () => ({ Component: (await import('./pages/Privacy')).Goodbye }) },
  // A reminder email's "Stop these emails": no sign-in.
  { path: '/unsubscribe', lazy: async () => ({ Component: (await import('./pages/Unsubscribe')).Unsubscribe }) },
  { path: '/privacy', lazy: async () => ({ Component: (await import('./pages/Docs')).PrivacyNotice }) },
  { path: '/terms', lazy: async () => ({ Component: (await import('./pages/Docs')).Terms }) },
  { path: '/admin/keys', lazy: async () => ({ Component: (await import('./pages/AdminKeys')).AdminKeys }) },
  // The admin console (gap 2): signed in, with a role that covers the screen (each says so if not).
  ...(
    [
      ['/admin', 'AdminHome'],
      ['/admin/privacy-requests', 'PrivacyRequests'],
      ['/admin/privacy-requests/:id', 'AnswerRequest'],
      ['/admin/breach', 'BreachNotice'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/Admin'))[name];
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  ...(
    [
      ['/admin/due-dates', 'DueDates'],
      ['/admin/flags', 'Flags'],
      ['/admin/admins', 'Admins'],
      ['/admin/status', 'StatusUpdates'],
    ] as const
  ).map(([path, name]) => ({
    path,
    lazy: async () => {
      const Screen = (await import('./pages/AdminRun'))[name];
      return {
        Component: () => (
          <RequireSignedIn>
            <Screen />
          </RequireSignedIn>
        ),
      };
    },
  })),
  // Help and the status page (gap 7): no sign-in, for anyone stuck signing in too.
  { path: '/help', lazy: async () => ({ Component: (await import('./pages/Help')).Help }) },
  { path: '/status', lazy: async () => ({ Component: (await import('./pages/Help')).StatusPage }) },
  { path: '*', element: <NotFound /> },
];

/** When the server refuses the sign-in itself (signed out elsewhere, blocked), the app signs out too. */
function onApiError(error: unknown) {
  if (isSignInRefused(error)) window.dispatchEvent(new Event(SIGN_IN_REFUSED_EVENT));
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({ onError: onApiError }),
    mutationCache: new MutationCache({ onError: onApiError }),
    defaultOptions: { queries: { refetchOnWindowFocus: false } },
  });
}

const router = createBrowserRouter(routes);
const queryClient = createQueryClient();

// A notification tapped while EkApp is open: the service worker asks the page to go there (src/sw/sw.ts).
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: unknown; path?: unknown } | null;
    if (
      data?.type === 'oneapp:open' &&
      typeof data.path === 'string' &&
      data.path.startsWith('/') &&
      !data.path.startsWith('//')
    ) {
      void router.navigate(data.path);
    }
  });
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
